import { computed, type ComputedRef } from 'vue';

import type {
  HelperCampaignCriteriaViewModel,
  HelperCampaignToJoin,
  HelperCampaignViewModel,
} from '../types';

function toCampaignCriteria(
  campaign: HelperCampaignToJoin
): HelperCampaignCriteriaViewModel[] {
  return campaign.criteria.map((criteria) => ({
    criteriaId: criteria.id,
    weight: criteria.weight,
    labelHelper: criteria.labelHelper,
    type: criteria.type,
    options: criteria.options,
  }));
}

function buildCampaignViewModel(
  campaignToJoin: HelperCampaignToJoin | null | undefined
): HelperCampaignViewModel {
  if (!campaignToJoin) {
    return {
      id: null,
      name: '',
      description: '',
      headerUrl: '',
      points: 0,
      campaignAdmin: null,
      helpersProfilePicture: [],
      helpersCount: 0,
      campaignCriteria: [],
    };
  }

  return {
    id: campaignToJoin.id,
    name: campaignToJoin.name,
    description: campaignToJoin.description,
    headerUrl: campaignToJoin.headerUrl ?? '',
    points: campaignToJoin.potentialPoints ?? 0,
    campaignAdmin: campaignToJoin.campaignAdmin ?? null,
    helpersProfilePicture: campaignToJoin.helpersProfilePicture ?? [],
    helpersCount: campaignToJoin.helpersCount ?? 0,
    campaignCriteria: toCampaignCriteria(campaignToJoin),
  };
}

export function useHelperCampaignViewModel(params: {
  campaignToJoin: HelperCampaignToJoin | null | undefined;
}): {
  campaign: ComputedRef<HelperCampaignViewModel>;
  campaignCriteria: ComputedRef<HelperCampaignCriteriaViewModel[]>;
  strongCriteriaIds: ComputedRef<number[]>;
} {
  const campaign = computed(() =>
    buildCampaignViewModel(params.campaignToJoin)
  );

  const campaignCriteria = computed(() => campaign.value.campaignCriteria);

  const strongCriteriaIds = computed(() =>
    campaignCriteria.value
      .filter((criteria) => criteria.weight === 'strong')
      .map((criteria) => criteria.criteriaId)
  );

  return {
    campaign,
    campaignCriteria,
    strongCriteriaIds,
  };
}
